/**
 * Shift matching and daily settlement container (C32, B4 D08 — D-120 – D-122, D-298, D-299; D-302 – D-304): binds
 * `Match` (packages/ui, old App.tsx:36353-37948), its four dialogs, the approval dialogs and the two reports to the
 * read models `GET /shifts` / `GET /days` and the write routes.
 *
 *   figures  : every figure is the server's — drawer figures from the ledger (D-298), the stored counts and their
 *              differences / approval state (D-299); this container only formats decimal strings and adds them for
 *              the cards that join two server figures (receipts + custody in, payments + expenses + custody out).
 *   actions  : open / close a shift (own, or another user's with `open_shift_for_other`, D-120, D-303), open / close
 *              the day (office today only, D-302) — counted IQD / USD typed, no pre-fill (D-120) → own PIN → API;
 *              approve (optional custody for a day count, D-299 (e)) / reject (reason) a difference → own PIN (D-304).
 *   prints   : the opening print (old "تقرير افتتاح وردية موظف", App.tsx:983-1079) in the A4 statement template, the
 *              2-page shift report and the 3-page day report rendered in the print root; every print / PDF / share
 *              is reported to the API first (D-147 pattern: a reprint is a copy).
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import {
  ApproveDifferenceDialog, CloseDayModal, CloseShiftModal, DayReportModal, DayReportPages, Match, OpenDayModal, OpenShiftModal, RejectDifferenceDialog, ShiftReportModal,
  ShiftReportPages,
  type DayReportData, type DifferenceSummary, type MatchActiveDay, type MatchActiveShift, type MatchCountStatus, type MatchCountValues, type MatchDayRow, type MatchDiff,
  type MatchGridRow, type MatchMoney, type MatchPendingRow, type MatchShiftRow, type MatchTab, type ReportCountLine, type ReportOperation, type ReportRow, type ShiftReportData,
} from "@faylasuf/ui";
import type {
  CashCountView, CurrencyPair, DayReportView, DayRowView, DrawerFiguresView, ReportBoxLine, ReportOperationLine, ShiftLiveView, ShiftReportView, ShiftRowView,
} from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { errorMessage, isSessionLost } from "../app/errors.js";
import { usePinGate } from "../app/pin-gate.js";
import { usePrintOffice } from "../app/printOffice.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { decAdd, decCmp, decSub, fmt, isZero } from "./customers/format.js";
import { useHypotheses } from "./match/hypotheses.js";

const EMPTY_COUNT: MatchCountValues = { iqd: "", usd: "", note: "" };
const money = (p: CurrencyPair): MatchMoney => ({ iqd: fmt(p.IQD), usd: fmt(p.USD) });
const signed = (v: string): string => (decCmp(v, "0") > 0 ? `+${fmt(v)}` : fmt(v));
const sum = (...v: string[]): string => v.reduce((a, b) => decAdd(a, b), "0");
const short = (id: string): string => id.replace(/-/g, "").slice(-6).toUpperCase();

/** Inflow / outflow cards of a drawer: receipts + custody in; payments + expenses + custody out (D-122 rows). */
const inOut = (d: DrawerFiguresView) => ({ in: sum(d.receipts, d.custodyIn), out: sum(d.payments, d.expenses, d.custodyOut) });

export function MatchPage() {
  const { t, locale, source } = useApp();
  const { session, hasPermission, isSuperManager, sessionLost } = useSession();
  const printOffice = usePrintOffice();
  const toast = useToast();
  const qc = useQueryClient();
  const pin = usePinGate();
  const tz = session.office.timeZone;
  const lang = locale;
  const showUsd = hasPermission("dollar");
  const isAdmin = isSuperManager || session.user.roleBuiltin === "branch_manager";

  const [tab, setTab] = useState<MatchTab>("shifts");
  const [employee, setEmployee] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const shiftsQ = useQuery({
    queryKey: [...keys.shifts, employee, from, to],
    queryFn: () => source.shifts({ ...(employee ? { employee } : {}), ...(from ? { from } : {}), ...(to ? { to } : {}) }),
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });
  const daysQ = useQuery({ queryKey: keys.days, queryFn: () => source.days({}), staleTime: 5_000, enabled: tab === "daily" || shiftsQ.isSuccess });
  if ((shiftsQ.error && isSessionLost(shiftsQ.error)) || (daysQ.error && isSessionLost(daysQ.error))) sessionLost();
  const sv = shiftsQ.data;
  const dv = daysQ.data;

  const fail = (e: unknown) => {
    if (isSessionLost(e)) sessionLost();
    else toast(errorMessage(t, e), "error");
  };
  const invalidate = () => {
    for (const k of [keys.shifts, keys.days, keys.accounts, keys.dashboard, keys.journal, keys.profits]) void qc.invalidateQueries({ queryKey: k });
  };

  // ---------------------------------------------------------------- labels (office time zone, D-122)
  const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(lang === "ar" ? "ar-IQ" : "en-US", { hour: "2-digit", minute: "2-digit", timeZone: tz });
  const dateOf = (iso: string) => new Date(iso).toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-US", { timeZone: tz });
  const dateTimeOf = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-IQ" : "en-US", { timeZone: tz });
  const diffOf = (v: string, status: MatchCountStatus | null): MatchDiff => ({ label: signed(v), negative: decCmp(v, "0") < 0, zero: isZero(v), status });
  const amountLabel = (v: string, usd: boolean) => (usd ? `$${fmt(v)}` : `${fmt(v)} د.ع`);
  const scopeKey = (s: CashCountView["scope"]) => `mt.scope_${s}`;
  const statusLabel = (c: CashCountView) => (c.status === "approved" && c.operationCancelled ? t("mt.status_cancelled_after") : t(`mt.status_${c.status}`));

  // ---------------------------------------------------------------- shifts tab
  const activeShifts: MatchActiveShift[] = useMemo(
    () =>
      (sv?.active ?? []).map((a: ShiftLiveView) => {
        const iqd = inOut(a.drawers.IQD);
        const usd = inOut(a.drawers.USD);
        const adjI = sum(a.drawers.IQD.differences, a.drawers.IQD.corrections);
        const adjU = sum(a.drawers.USD.differences, a.drawers.USD.corrections);
        return {
          id: a.id,
          owner: a.owner,
          startedLabel: dateTimeOf(a.openedAt),
          opening: { iqd: fmt(a.drawers.IQD.opening), usd: fmt(a.drawers.USD.opening) },
          other: money(a.otherOpening),
          receipts: { iqd: fmt(iqd.in), usd: fmt(usd.in) },
          outflows: { iqd: fmt(iqd.out), usd: fmt(usd.out) },
          expected: { iqd: fmt(a.drawers.IQD.expected), usd: fmt(a.drawers.USD.expected) },
          adjustments: isZero(adjI) && isZero(adjU) ? null : { iqd: signed(adjI), usd: signed(adjU) },
          pending: isZero(a.drawers.IQD.pending) && isZero(a.drawers.USD.pending) ? null : { iqd: signed(a.drawers.IQD.pending), usd: signed(a.drawers.USD.pending) },
          operationsCount: a.operationsCount,
          expensesCount: a.expensesCount,
          profitsIqd: fmt(a.profits.IQD),
          profitsUsd: isZero(a.profits.USD) ? null : fmt(a.profits.USD),
          boxes: a.boxes
            .reduce<Array<{ name: string; inIqd: string; outIqd: string; inUsd: string; outUsd: string }>>((acc, b) => {
              let row = acc.find((x) => x.name === b.name);
              if (!row) {
                row = { name: b.name, inIqd: "0", outIqd: "0", inUsd: "0", outUsd: "0" };
                acc.push(row);
              }
              if (b.currency === "USD") {
                row.inUsd = decAdd(row.inUsd, b.receipts);
                row.outUsd = decAdd(row.outUsd, b.payments);
              } else {
                row.inIqd = decAdd(row.inIqd, b.receipts);
                row.outIqd = decAdd(row.outIqd, b.payments);
              }
              return acc;
            }, [])
            .map((r) => ({ name: r.name, inIqd: fmt(r.inIqd), outIqd: fmt(r.outIqd), inUsd: fmt(r.inUsd), outUsd: fmt(r.outUsd), hasUsd: !isZero(r.inUsd) || !isZero(r.outUsd) })),
          canClose: a.ownerUserId === session.user.id || hasPermission("open_shift_for_other"),
        };
      }),
    [sv, lang],
  );
  const shiftRows: MatchShiftRow[] = (sv?.log ?? []).map((s: ShiftRowView) => ({
    id: s.id,
    owner: s.owner,
    startTime: timeOf(s.openedAt),
    startDate: dateOf(s.openedAt),
    endTime: s.closedAt ? timeOf(s.closedAt) : null,
    open: s.open,
    opening: money(s.opening),
    expected: money(s.expected),
    counted: s.counted ? money(s.counted) : null,
    diffIqd: diffOf(s.open ? s.openingDifference.IQD : s.difference.IQD, s.open ? null : s.differenceStatus.IQD),
    diffUsd: diffOf(s.open ? s.openingDifference.USD : s.difference.USD, s.open ? null : s.differenceStatus.USD),
    profitsIqd: fmt(s.profits.IQD),
  }));
  const mt = sv?.monthTotals;
  const monthTotals = mt
    ? {
        opening: money(mt.opening),
        closing: money(mt.closing),
        profits: money(mt.profits),
        netIqd: signed(mt.net.IQD),
        netIqdNegative: decCmp(mt.net.IQD, "0") < 0,
        netUsd: decCmp(mt.net.USD, "0") < 0 ? `-$${fmt(mt.net.USD.replace(/^-/, ""))}` : `+$${fmt(mt.net.USD)}`,
        netUsdNegative: decCmp(mt.net.USD, "0") < 0,
      }
    : null;
  const canOpenShift = !!sv && sv.users.some((u) => !u.hasOpenShift);

  // ---------------------------------------------------------------- daily tab
  const [gridDayId, setGridDayId] = useState<string | null>(null);
  const todayLive = dv?.today && dv.today.day.open && !dv.today.day.expired ? dv.today : null;
  useEffect(() => setGridDayId(isAdmin && todayLive ? todayLive.day.id : null), [isAdmin, todayLive?.day.id]);
  const gridQ = useQuery({ queryKey: keys.dayReport(gridDayId ?? "-"), queryFn: () => source.dayReport(gridDayId!), enabled: !!gridDayId && tab === "daily", staleTime: 5_000 });
  const gridRows = (r: DayReportView): MatchGridRow[] => {
    const rows: MatchGridRow[] = [];
    const push = (key: string, name: string, usd: boolean, op: string, cl: string) => {
      const delta = decSub(cl, op);
      const sign = decCmp(delta, "0") as 1 | 0 | -1;
      rows.push({ key, name, usd, opening: fmt(op), current: fmt(cl), delta: usd ? (sign > 0 ? `+$${fmt(delta)}` : sign < 0 ? `-$${fmt(delta.replace(/^-/, ""))}` : "$0") : sign > 0 ? `+${fmt(delta)}` : fmt(delta), deltaSign: sign });
    };
    push("desk_iqd", t("mt.desk_iqd"), false, r.totals.IQD.opening, r.totals.IQD.expected);
    if (showUsd && (!isZero(r.totals.USD.opening) || !isZero(r.totals.USD.expected))) push("desk_usd", t("mt.desk_usd"), true, r.totals.USD.opening, r.totals.USD.expected);
    if (hasPermission("wallets"))
      for (const b of r.boxes) if (b.currency === "IQD" || (showUsd && b.currency === "USD")) push(`b_${b.accountId}`, b.currency === "USD" ? `${b.name} (USD)` : b.name, b.currency === "USD", b.opening, b.closing);
    if (hasPermission("debts")) {
      push("debts_iqd", t("mt.debts_iqd"), false, r.debts.opening.IQD, r.debts.closing.IQD);
      if (showUsd && (!isZero(r.debts.opening.USD) || !isZero(r.debts.closing.USD))) push("debts_usd", t("mt.debts_usd"), true, r.debts.opening.USD, r.debts.closing.USD);
      push("trusts_iqd", t("mt.trusts_iqd"), false, r.trusts.opening.IQD, r.trusts.closing.IQD);
      if (showUsd && (!isZero(r.trusts.opening.USD) || !isZero(r.trusts.closing.USD))) push("trusts_usd", t("mt.trusts_usd"), true, r.trusts.opening.USD, r.trusts.closing.USD);
    }
    if (hasPermission("financials")) {
      push("exp", t("mt.day_expenses"), false, "0", r.totals.IQD.expenses);
      push("pay", t("mt.day_payments"), false, "0", r.totals.IQD.payments);
    }
    return rows;
  };
  const activeDay: MatchActiveDay | null = todayLive
    ? (() => {
        const i = inOut(todayLive.totals.IQD);
        const u = inOut(todayLive.totals.USD);
        return {
          dateLabel: todayLive.day.officeDate,
          opening: { iqd: fmt(todayLive.totals.IQD.opening), usd: fmt(todayLive.totals.USD.opening) },
          receipts: { iqd: fmt(i.in), usd: fmt(u.in) },
          outflows: { iqd: fmt(i.out), usd: fmt(u.out) },
          expected: { iqd: fmt(todayLive.totals.IQD.expected), usd: fmt(todayLive.totals.USD.expected) },
          grid: isAdmin && gridQ.data ? gridRows(gridQ.data) : null,
        };
      })()
    : null;
  const dayRows: MatchDayRow[] = (dv?.log ?? []).map((d: DayRowView) => ({
    id: d.id,
    date: d.officeDate,
    open: d.open,
    expired: d.expired,
    opening: money(d.opening),
    expected: money(d.expected),
    counted: d.counted ? money(d.counted) : null,
    diffIqd: diffOf(d.difference.IQD, d.differenceStatus.IQD),
    diffUsd: diffOf(d.difference.USD, d.differenceStatus.USD),
  }));

  // ---------------------------------------------------------------- pending differences (D-304)
  const pendingAll: CashCountView[] = useMemo(() => {
    const m = new Map<string, CashCountView>();
    for (const c of [...(sv?.pendingCounts ?? []), ...(dv?.pendingCounts ?? [])]) m.set(c.id, c);
    return [...m.values()].sort((a, b) => a.countedAt.localeCompare(b.countedAt));
  }, [sv, dv]);
  const canApprove = hasPermission("approve_cash_differences");
  const pendingRows: MatchPendingRow[] = pendingAll.map((c) => ({
    id: c.id,
    scopeKey: scopeKey(c.scope),
    reopened: c.reopenedFromId !== null,
    responsible: c.responsible,
    countedAtLabel: dateTimeOf(c.countedAt),
    currencyUsd: c.currency === "USD",
    expected: fmt(c.expected),
    counted: fmt(c.counted),
    difference: signed(c.difference),
    negative: decCmp(c.difference, "0") < 0,
    canDecide: canApprove && c.responsibleUserId !== session.user.id && (c.countedByUserId ?? "") !== session.user.id && c.countedBy !== session.user.username,
  }));
  const summaryOf = (c: CashCountView): DifferenceSummary => ({
    scopeLabel: `${t(scopeKey(c.scope))} — ${c.currency}`,
    responsible: c.responsible,
    expected: amountLabel(c.expected, c.currency === "USD"),
    counted: amountLabel(c.counted, c.currency === "USD"),
    difference: c.currency === "USD" ? `${decCmp(c.difference, "0") > 0 ? "+" : "-"}$${fmt(c.difference.replace(/^-/, ""))}` : `${signed(c.difference)} د.ع`,
    negative: decCmp(c.difference, "0") < 0,
  });

  // ---------------------------------------------------------------- dialogs
  const [busy, setBusy] = useState(false);
  const [openShift, setOpenShift] = useState(false);
  const [owner, setOwner] = useState("");
  const [openValues, setOpenValues] = useState<MatchCountValues>(EMPTY_COUNT);
  const drawersQ = useQuery({ queryKey: ["shift-drawers", owner], queryFn: () => source.shiftDrawers(owner), enabled: openShift && !!owner, staleTime: 2_000 });
  const [closeShiftId, setCloseShiftId] = useState<string | null>(null);
  const [closeValues, setCloseValues] = useState<MatchCountValues>(EMPTY_COUNT);
  const [openDay, setOpenDay] = useState(false);
  const [dayValues, setDayValues] = useState<MatchCountValues>(EMPTY_COUNT);
  const [closeDay, setCloseDay] = useState(false);
  const [closeDayValues, setCloseDayValues] = useState<MatchCountValues>(EMPTY_COUNT);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState("");
  const [rejectId, setRejectId] = useState<string | null>(null);
  const counted = (v: MatchCountValues) => ({ IQD: v.iqd.trim(), USD: v.usd.trim() });
  const complete = (v: MatchCountValues) => v.iqd.trim() !== "" && v.usd.trim() !== "";

  const reportCounts = (counts: readonly CashCountView[]) => {
    const pend = counts.filter((c) => c.status === "pending").length;
    if (pend > 0) toast(t("mt.toast_pending_diff"), "success");
  };

  const ownerName = (id: string) => sv?.users.find((u) => u.id === id)?.username ?? session.user.username;
  const dr = drawersQ.data;
  const drawerLine = (d: DrawerFiguresView, value: string) => {
    const usd = d.currency === "USD";
    const diff = value.trim() === "" ? null : decSub(value.trim(), d.countBasis);
    return { name: d.accountName ?? (usd ? "USD" : "IQD"), usd, book: fmt(d.countBasis), difference: diff && !isZero(diff) ? signed(diff) : null, negative: !!diff && decCmp(diff, "0") < 0 };
  };
  const netDiff = dr
    ? (() => {
        const i = openValues.iqd.trim() === "" ? "0" : decSub(openValues.iqd.trim(), dr.drawers.IQD.countBasis);
        const u = openValues.usd.trim() === "" ? "0" : decSub(openValues.usd.trim(), dr.drawers.USD.countBasis);
        return isZero(i) && isZero(u) ? null : { iqd: signed(i), usd: signed(u) };
      })()
    : null;

  // opening print (old getShiftOpeningPrintPayload) after the open dialog's print / share buttons
  const [openingPrint, setOpeningPrint] = useState<{ shiftId: string; owner: string; at: string; note: string; rows: Array<Record<string, string>> } | null>(null);
  const submitOpenShift = async (mode: "open" | "share" | "print") => {
    if (!owner) return toast(t("mt.err_owner"), "error");
    if (!complete(openValues)) return toast(t("mt.err_counted"), "error");
    setBusy(true);
    try {
      const body = { ownerUserId: owner, counted: counted(openValues), ...(openValues.note.trim() ? { note: openValues.note.trim() } : {}) };
      const res = await pin.withPin((proof) => source.openShift({ ...body, pin: proof.pin ?? "" }), {
        message: t("mt.pin_open", { name: ownerName(owner), iqd: fmt(body.counted.IQD), usd: fmt(body.counted.USD) }),
      });
      if (!res) return;
      toast(t("mt.toast_opened"), "success");
      reportCounts(res.counts);
      if (mode !== "open" && dr) {
        await source.shiftPrinted(res.shiftId, "opening", mode === "share" ? "whatsapp" : "print").catch(() => undefined);
        const row = (name: string, d: DrawerFiguresView, c: string) => {
          const diff = decSub(c, d.countBasis);
          const usd = d.currency === "USD";
          return { name, newBal: amountLabel(c, usd), oldBal: amountLabel(d.countBasis, usd), diff: isZero(diff) ? "-" : usd ? `${decCmp(diff, "0") > 0 ? "+" : "-"}$${fmt(diff.replace(/^-/, ""))}` : `${signed(diff)} د.ع` };
        };
        setOpeningPrint({
          shiftId: res.shiftId,
          owner: ownerName(owner),
          at: dateTimeOf(new Date().toISOString()),
          note: body.note ?? "-",
          rows: [
            row(t("mt.op_till_iqd"), dr.drawers.IQD, body.counted.IQD),
            row(t("mt.op_till_usd"), dr.drawers.USD, body.counted.USD),
            { name: t("mt.op_other_iqd"), newBal: amountLabel(dr.otherOpening.IQD, false), oldBal: amountLabel(dr.otherOpening.IQD, false), diff: "-" },
            { name: t("mt.op_other_usd"), newBal: amountLabel(dr.otherOpening.USD, true), oldBal: amountLabel(dr.otherOpening.USD, true), diff: "-" },
          ],
        });
      }
      setOpenShift(false);
      setOpenValues(EMPTY_COUNT);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const closingShift = sv?.active.find((a) => a.id === closeShiftId) ?? null;
  const submitCloseShift = async () => {
    if (!closingShift) return;
    if (!complete(closeValues)) return toast(t("mt.err_counted"), "error");
    setBusy(true);
    try {
      const c = counted(closeValues);
      const res = await pin.withPin((proof) => source.closeShift(closingShift.id, { counted: c, ...(closeValues.note.trim() ? { note: closeValues.note.trim() } : {}), pin: proof.pin ?? "" }), {
        message: t("mt.pin_close", { name: closingShift.owner, iqd: fmt(c.IQD), usd: fmt(c.USD) }),
      });
      if (!res) return;
      toast(t("mt.toast_closed"), "success");
      reportCounts(res.counts);
      setCloseShiftId(null);
      setCloseValues(EMPTY_COUNT);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const submitOpenDay = async () => {
    if (!complete(dayValues)) return toast(t("mt.err_counted"), "error");
    setBusy(true);
    try {
      const c = counted(dayValues);
      const res = await pin.withPin((proof) => source.openDay({ counted: c, ...(dayValues.note.trim() ? { note: dayValues.note.trim() } : {}), pin: proof.pin ?? "" }), {
        message: t("mt.pin_day_open", { date: dv?.officeToday ?? "", iqd: fmt(c.IQD), usd: fmt(c.USD) }),
      });
      if (!res) return;
      toast(t("mt.toast_day_opened"), "success");
      reportCounts(res.counts);
      setOpenDay(false);
      setDayValues(EMPTY_COUNT);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const submitCloseDay = async () => {
    if (!todayLive) return;
    if (!complete(closeDayValues)) return toast(t("mt.err_counted"), "error");
    setBusy(true);
    try {
      const c = counted(closeDayValues);
      const res = await pin.withPin((proof) => source.closeDay(todayLive.day.id, { counted: c, ...(closeDayValues.note.trim() ? { note: closeDayValues.note.trim() } : {}), pin: proof.pin ?? "" }), {
        message: t("mt.pin_day_close", { date: todayLive.day.officeDate, iqd: fmt(c.IQD), usd: fmt(c.USD) }),
      });
      if (!res) return;
      toast(t("mt.toast_day_closed"), "success");
      reportCounts(res.counts);
      setCloseDay(false);
      setCloseDayValues(EMPTY_COUNT);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const approving = pendingAll.find((c) => c.id === approveId) ?? null;
  const targetsQ = useQuery({ queryKey: keys.dayReport(approving?.dayId ?? "-"), queryFn: () => source.dayReport(approving!.dayId!), enabled: !!approving?.dayId });
  const approveTargets =
    approving?.dayId && targetsQ.data ? targetsQ.data.drawers.filter((d) => d.currency === approving.currency).map((d) => ({ id: d.accountId, name: d.accountName ?? d.accountId })) : [];
  const confirmApprove = async () => {
    if (!approving) return;
    setBusy(true);
    try {
      const res = await pin.withPin(
        (proof) => source.approveCount(approving.id, { idempotencyKey: crypto.randomUUID(), ...(approveTarget ? { postToAccountId: approveTarget } : {}), pin: proof.pin ?? "" }),
        { message: t("mt.pin_approve", { amount: summaryOf(approving).difference, name: approving.responsible }) },
      );
      if (!res) return;
      toast(t("mt.toast_approved"), "success");
      setApproveId(null);
      setApproveTarget("");
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const rejecting = pendingAll.find((c) => c.id === rejectId) ?? null;
  const confirmReject = async (reason: string) => {
    if (!rejecting) return;
    setBusy(true);
    try {
      const res = await pin.withPin((proof) => source.rejectCount(rejecting.id, { reason, pin: proof.pin ?? "" }), {
        message: t("mt.pin_reject", { amount: summaryOf(rejecting).difference, name: rejecting.responsible }),
      });
      if (!res) return;
      toast(t("mt.toast_rejected"), "success");
      if (res.reopened.length > 0) toast(t("mt.toast_reopened", { n: res.reopened.length }), "success");
      setRejectId(null);
      invalidate();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- reports
  const [shiftReportId, setShiftReportId] = useState<string | null>(null);
  const [dayReportId, setDayReportId] = useState<string | null>(null);
  const shiftRepQ = useQuery({ queryKey: keys.shiftReport(shiftReportId ?? "-"), queryFn: () => source.shiftReport(shiftReportId!), enabled: !!shiftReportId });
  const canPrintDay = !!dv?.me.canPrint || isSuperManager;
  const dayRepQ = useQuery({ queryKey: keys.dayReport(dayReportId ?? "-"), queryFn: () => source.dayReport(dayReportId!), enabled: !!dayReportId && (isAdmin || canPrintDay) });
  useEffect(() => {
    if (shiftRepQ.error) fail(shiftRepQ.error);
  }, [shiftRepQ.error]);
  useEffect(() => {
    if (dayRepQ.error) fail(dayRepQ.error);
  }, [dayRepQ.error]);
  const [copy, setCopy] = useState(false);

  const cell = (text: string, cls?: string) => ({ text, ...(cls ? { cls } : {}) });
  const tone = (v: string, up: string, down: string) => (decCmp(v, "0") > 0 ? up : decCmp(v, "0") < 0 ? down : "text-slate-400");
  const boxRows = (boxes: readonly ReportBoxLine[]): ReportRow[] =>
    boxes.map((b) => {
      const usd = b.currency === "USD";
      return {
        key: `b_${b.accountId}`,
        name: b.name,
        opening: [cell(amountLabel(b.opening, usd))],
        closing: [cell(amountLabel(b.closing, usd))],
        variance: [cell(`${decCmp(b.variance, "0") > 0 ? "+" : ""}${amountLabel(b.variance, usd)}`, tone(b.variance, "text-emerald-600", "text-rose-600"))],
      };
    });
  const pairRow = (key: string, name: string, op: CurrencyPair, cl: CurrencyPair, up: string, down: string, extra: Partial<ReportRow> = {}): ReportRow => {
    const di = decSub(cl.IQD, op.IQD);
    const du = decSub(cl.USD, op.USD);
    const hasUsd = !isZero(op.USD) || !isZero(cl.USD);
    return {
      key,
      name,
      opening: [cell(`${fmt(op.IQD)} د.ع`), ...(hasUsd ? [cell(`$${fmt(op.USD)}`, "text-[10px] text-slate-400")] : [])],
      closing: [cell(`${fmt(cl.IQD)} د.ع`), ...(hasUsd ? [cell(`$${fmt(cl.USD)}`, "text-[10px] text-slate-400")] : [])],
      variance: [cell(`${decCmp(di, "0") > 0 ? "+" : ""}${fmt(di)} د.ع`, tone(di, up, down)), ...(hasUsd ? [cell(`${decCmp(du, "0") > 0 ? "+" : ""}$${fmt(du)}`, `text-[10px] ${tone(du, up, down)}`)] : [])],
      ...extra,
    };
  };
  const countsLines = (counts: readonly CashCountView[]): ReportCountLine[] =>
    counts.map((c) => ({ key: c.id, scope: `${t(scopeKey(c.scope))} (${c.currency})`, expected: amountLabel(c.expected, c.currency === "USD"), counted: amountLabel(c.counted, c.currency === "USD"), difference: signed(c.difference), negative: decCmp(c.difference, "0") < 0, status: statusLabel(c) }));
  const opLines = (lines: readonly ReportOperationLine[]): ReportOperation[] =>
    lines.map((l) => {
      const net = decAdd(l.drawer.IQD, "0");
      const inflow = decCmp(net, "0") > 0 || (isZero(net) && decCmp(l.drawer.USD, "0") > 0);
      const parts = [!isZero(l.drawer.IQD) ? `${signed(l.drawer.IQD)} د.ع` : null, !isZero(l.drawer.USD) ? `${decCmp(l.drawer.USD, "0") > 0 ? "+" : "-"}$${fmt(l.drawer.USD.replace(/^-/, ""))}` : null].filter(Boolean);
      return { key: l.operationId, typeLabel: t(`jr.type.${l.type}`), timeLabel: timeOf(l.at), description: l.operator ?? "", inflow, amountLabel: parts.join(" / ") || "0", correction: l.correction };
    });
  const shiftReport: ShiftReportData | null = useMemo(() => {
    const r: ShiftReportView | undefined = shiftRepQ.data;
    if (!r) return null;
    const s = r.shift;
    const zeroPair = { IQD: "0", USD: "0" };
    const closingCash = s.counted ?? { IQD: r.drawers.IQD.expected, USD: r.drawers.USD.expected };
    const vault = (cur: "IQD" | "USD"): ReportRow => {
      const d = r.drawers[cur];
      const cl = cur === "IQD" ? closingCash.IQD : closingCash.USD;
      const diff = decSub(cl, d.opening);
      const usd = cur === "USD";
      return {
        key: `vault_${cur}`,
        name: t(usd ? "mt.rep_vault_usd" : "mt.rep_vault_iqd"),
        nameCls: "text-slate-900 dark:text-white",
        rowCls: usd ? "font-bold bg-slate-50/30 dark:bg-slate-900/20" : "font-bold",
        opening: [cell(amountLabel(d.opening, usd))],
        closing: [cell(amountLabel(cl, usd))],
        variance: [cell(`${decCmp(diff, "0") > 0 ? "+" : ""}${amountLabel(diff, usd)}`, tone(diff, "text-emerald-600", "text-rose-600"))],
      };
    };
    const rows: ReportRow[] = [vault("IQD"), vault("USD"), ...boxRows(r.boxes)];
    rows.push(pairRow("debts", t("mt.rep_debts"), r.debts.opening, r.debts.closing, "text-rose-600", "text-emerald-600", { rowCls: "bg-rose-50/20 dark:bg-rose-950/10", nameCls: "text-rose-700 dark:text-rose-400" }));
    rows.push(pairRow("trusts", t("mt.rep_trusts"), r.trusts.opening, r.trusts.closing, "text-amber-600", "text-slate-500", { rowCls: "bg-amber-50/20 dark:bg-amber-950/10", nameCls: "text-amber-700 dark:text-amber-400" }));
    const exp = { IQD: r.drawers.IQD.expenses, USD: r.drawers.USD.expenses };
    if (!isZero(exp.IQD) || !isZero(exp.USD))
      rows.push({ key: "expenses", name: t("mt.rep_expenses"), nameCls: "text-slate-700 dark:text-slate-300", opening: [cell("-", "text-slate-400")], closing: [cell(`${fmt(exp.IQD)} د.ع`, "text-rose-600"), ...(!isZero(exp.USD) ? [cell(`$${fmt(exp.USD)}`, "text-[10px] text-rose-600")] : [])], variance: [cell(`-${fmt(exp.IQD)} د.ع`, "text-rose-600"), ...(!isZero(exp.USD) ? [cell(`-$${fmt(exp.USD)}`, "text-[10px] text-rose-600")] : [])] });
    const pay = { IQD: sum(r.drawers.IQD.payments, r.drawers.IQD.custodyOut), USD: sum(r.drawers.USD.payments, r.drawers.USD.custodyOut) };
    if (!isZero(pay.IQD) || !isZero(pay.USD))
      rows.push({ key: "payments", name: t("mt.rep_payments"), nameCls: "text-slate-700 dark:text-slate-300", opening: [cell("-", "text-slate-400")], closing: [cell(`${fmt(pay.IQD)} د.ع`, "text-rose-600"), ...(!isZero(pay.USD) ? [cell(`$${fmt(pay.USD)}`, "text-[10px] text-rose-600")] : [])], variance: [cell(`-${fmt(pay.IQD)} د.ع`, "text-rose-600"), ...(!isZero(pay.USD) ? [cell(`-$${fmt(pay.USD)}`, "text-[10px] text-rose-600")] : [])] });
    rows.push({ key: "profit", name: t("mt.rep_profit"), rowCls: "bg-emerald-50/30 dark:bg-emerald-950/20 font-black", nameCls: "text-emerald-800 dark:text-emerald-400", opening: [cell("-", "text-slate-400")], closing: [cell(`+${fmt(r.profits.IQD)} د.ع`, "text-emerald-600"), ...(!isZero(r.profits.USD) ? [cell(`+$${fmt(r.profits.USD)}`, "text-[10px] text-emerald-600")] : [])], variance: [cell(`+${fmt(r.profits.IQD)} د.ع`, "text-emerald-600"), ...(!isZero(r.profits.USD) ? [cell(`+$${fmt(r.profits.USD)}`, "text-[10px] text-emerald-600")] : [])] });
    const d = s.open ? zeroPair : s.difference;
    return {
      shiftNo: short(s.id),
      owner: s.owner,
      open: s.open,
      openedLabel: dateTimeOf(s.openedAt),
      closedLabel: s.closedAt ? dateTimeOf(s.closedAt) : null,
      generatedLabel: dateOf(new Date().toISOString()),
      officeName: printOffice.name,
      rows,
      theo: money(s.expected),
      actual: money(closingCash),
      diff: { zero: isZero(d.IQD) && isZero(d.USD), iqd: signed(d.IQD), usd: isZero(d.USD) ? null : `${decCmp(d.USD, "0") > 0 ? "+" : "-"}$${fmt(d.USD.replace(/^-/, ""))}` },
      counts: countsLines(r.counts),
      note: s.note,
      operations: opLines(r.appendix),
      copy,
    };
  }, [shiftRepQ.data, lang, copy, printOffice.name]);
  const dayReport: DayReportData | null = useMemo(() => {
    const r: DayReportView | undefined = dayRepQ.data;
    if (!r) return null;
    const day = r.day;
    const rows: ReportRow[] = [
      pairRow("cash_iqd", t("mt.drep_cash_iqd"), { IQD: r.totals.IQD.opening, USD: "0" }, { IQD: day.counted?.IQD ?? r.totals.IQD.expected, USD: "0" }, "text-emerald-600", "text-rose-600", { nameCls: "text-blue-600 dark:text-blue-400 font-extrabold" }),
      { ...pairRow("cash_usd", t("mt.drep_cash_usd"), { IQD: "0", USD: r.totals.USD.opening }, { IQD: "0", USD: day.counted?.USD ?? r.totals.USD.expected }, "text-emerald-500", "text-rose-500"), opening: [cell(`$${fmt(r.totals.USD.opening)}`)], closing: [cell(`$${fmt(day.counted?.USD ?? r.totals.USD.expected)}`)] },
      ...boxRows(r.boxes),
      pairRow("debts", t("mt.drep_debts_iqd"), r.debts.opening, r.debts.closing, "text-emerald-600", "text-rose-600"),
      pairRow("trusts", t("mt.drep_trusts_iqd"), r.trusts.opening, r.trusts.closing, "text-emerald-600", "text-rose-600"),
      pairRow("exp", t("mt.day_expenses"), { IQD: "0", USD: "0" }, { IQD: r.totals.IQD.expenses, USD: r.totals.USD.expenses }, "text-rose-600", "text-emerald-600"),
      pairRow("pay", t("mt.day_payments"), { IQD: "0", USD: "0" }, { IQD: r.totals.IQD.payments, USD: r.totals.USD.payments }, "text-rose-600", "text-emerald-600"),
    ];
    const ops = opLines(r.appendix);
    return {
      dayNo: short(day.id),
      date: day.officeDate,
      closed: !day.open,
      officeName: printOffice.name,
      generatedLabel: dateTimeOf(new Date().toISOString()),
      rows,
      expected: { iqd: fmt(day.expected.IQD), usd: fmt(day.expected.USD) },
      actual: day.counted ? money(day.counted) : null,
      diff: { zero: isZero(day.difference.IQD) && isZero(day.difference.USD), iqdSurplus: decCmp(day.difference.IQD, "0") >= 0, iqd: signed(day.difference.IQD), usdSurplus: decCmp(day.difference.USD, "0") >= 0, usd: `$${fmt(day.difference.USD)}` },
      operationsCount: r.totals.IQD.operationsCount + r.totals.USD.operationsCount,
      expensesCount: r.appendix.filter((l) => l.type === "expense").length,
      note: day.note,
      counts: countsLines(r.counts),
      operations: ops,
      expenses: ops.filter((_, i) => r.appendix[i]!.type === "expense"),
      copy,
    };
  }, [dayRepQ.data, lang, copy, printOffice.name]);

  // print root: the report pages / the opening statement, then window.print()
  const [printing, setPrinting] = useState<"shift" | "day" | "opening" | null>(null);
  useEffect(() => {
    if (!printing) return;
    const done = () => {
      setPrinting(null);
      if (printing === "opening") setOpeningPrint(null);
    };
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);
  useEffect(() => {
    if (openingPrint) setPrinting("opening");
  }, [openingPrint]);
  const printReport = async (kind: "shift" | "day", channel: "print" | "pdf") => {
    const id = kind === "shift" ? shiftReportId : dayReportId;
    if (!id) return;
    setBusy(true);
    try {
      const r = kind === "shift" ? await source.shiftPrinted(id, "report", channel) : await source.dayPrinted(id, channel);
      setCopy(r.copy);
      setPrinting(kind);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  // [C34] D-308: the watchdog's explanations of the difference being decided
  const approveHypotheses = useHypotheses(approving ? approving.id : null);
  const rejectHypotheses = useHypotheses(rejecting ? rejecting.id : null);

  const openOwnerDefault = () => {
    const mine = sv?.users.find((u) => u.id === session.user.id && !u.hasOpenShift);
    const first = mine ?? sv?.users.find((u) => !u.hasOpenShift);
    setOwner(first?.id ?? "");
    setOpenValues(EMPTY_COUNT);
    setOpenShift(true);
  };

  return (
    <>
      <Match
        tab={tab}
        onTabChange={setTab}
        showUsd={showUsd}
        showWallets={hasPermission("wallets")}
        showProfits={hasPermission("financials")}
        showDebts={hasPermission("debts")}
        isAdmin={isAdmin}
        activeShifts={activeShifts}
        canOpenShift={canOpenShift}
        onOpenShift={openOwnerDefault}
        onCloseShift={(id) => {
          setCloseValues({ ...EMPTY_COUNT, note: sv?.active.find((a) => a.id === id)?.note ?? "" });
          setCloseShiftId(id);
        }}
        hasShifts={(sv?.log.length ?? 0) > 0 || !!employee || !!from || !!to}
        monthTotals={monthTotals}
        employees={(sv?.users ?? []).map((u) => ({ id: u.id, username: u.username }))}
        employeeFilter={employee}
        onEmployeeFilter={setEmployee}
        fromDate={from}
        onFromDate={setFrom}
        toDate={to}
        onToDate={setTo}
        onResetFilters={() => {
          setEmployee("");
          setFrom("");
          setTo("");
        }}
        shiftRows={shiftRows}
        onShiftReport={(id) => {
          setCopy(false);
          setShiftReportId(id);
        }}
        activeDay={activeDay}
        canOpenDay={!!dv && dv.today === null && dv.me.canSettle}
        onOpenDay={() => {
          setDayValues(EMPTY_COUNT);
          setOpenDay(true);
        }}
        onCloseDay={() => {
          setCloseDayValues({ ...EMPTY_COUNT, note: todayLive?.day.note ?? "" });
          setCloseDay(true);
        }}
        dayRows={dayRows}
        onDayReport={(id) => {
          setCopy(false);
          setDayReportId(id);
        }}
        pendingRows={pendingRows}
        onApprove={(id) => {
          setApproveTarget("");
          setApproveId(id);
        }}
        onReject={setRejectId}
      />
      <OpenShiftModal
        open={openShift}
        users={(sv?.users ?? []).map((u) => ({ id: u.id, username: u.username, disabled: u.hasOpenShift }))}
        ownerUserId={owner}
        onOwnerChange={setOwner}
        canChooseOwner={!!sv?.me.canOpenForOther}
        drawers={dr ? { iqd: drawerLine(dr.drawers.IQD, openValues.iqd), usd: drawerLine(dr.drawers.USD, openValues.usd) } : null}
        readOnly={dr ? { debts: { iqd: fmt(dr.debts.IQD), usd: fmt(dr.debts.USD) }, trusts: { iqd: fmt(dr.trusts.IQD), usd: fmt(dr.trusts.USD) }, profits: { iqd: fmt(dr.pendingProfits.IQD), usd: fmt(dr.pendingProfits.USD) } } : null}
        values={openValues}
        onChange={setOpenValues}
        netDifference={netDiff}
        busy={busy}
        onSubmit={(mode) => void submitOpenShift(mode)}
        onClose={() => setOpenShift(false)}
      />
      <CloseShiftModal
        open={closingShift !== null}
        expected={{ iqd: fmt(closingShift?.drawers.IQD.countBasis ?? "0"), usd: fmt(closingShift?.drawers.USD.countBasis ?? "0") }}
        values={closeValues}
        onChange={setCloseValues}
        busy={busy}
        onSubmit={() => void submitCloseShift()}
        onClose={() => setCloseShiftId(null)}
      />
      <OpenDayModal open={openDay} dateLabel={dv?.officeToday ?? ""} values={dayValues} onChange={setDayValues} busy={busy} onSubmit={() => void submitOpenDay()} onClose={() => setOpenDay(false)} />
      <CloseDayModal
        open={closeDay && todayLive !== null}
        expected={{ iqd: fmt(todayLive?.totals.IQD.countBasis ?? "0"), usd: fmt(todayLive?.totals.USD.countBasis ?? "0") }}
        values={closeDayValues}
        onChange={setCloseDayValues}
        busy={busy}
        onSubmit={() => void submitCloseDay()}
        onClose={() => setCloseDay(false)}
      />
      <ApproveDifferenceDialog
        hypotheses={approveHypotheses}
        open={approving !== null}
        summary={approving ? summaryOf(approving) : null}
        targets={approveTargets}
        target={approveTarget}
        onTargetChange={setApproveTarget}
        busy={busy}
        onConfirm={() => void confirmApprove()}
        onClose={() => setApproveId(null)}
      />
      <RejectDifferenceDialog hypotheses={rejectHypotheses} open={rejecting !== null} summary={rejecting ? summaryOf(rejecting) : null} busy={busy} onConfirm={(r) => void confirmReject(r)} onClose={() => setRejectId(null)} />
      <ShiftReportModal open={shiftReportId !== null} data={shiftReport} busy={busy} onPdf={() => void printReport("shift", "pdf")} onPrint={() => void printReport("shift", "print")} onClose={() => setShiftReportId(null)} />
      <DayReportModal
        open={dayReportId !== null}
        allowed={isAdmin || canPrintDay}
        data={dayReport}
        busy={busy}
        onPdf={() => void printReport("day", "pdf")}
        onPrint={() => void printReport("day", "print")}
        onClose={() => setDayReportId(null)}
      />
      <div className="relative z-[130]">{pin.dialog}</div>
      {printing &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            {printing === "opening" && openingPrint ? (
              <StatementA4
                locale={locale}
                title={t("mt.op_title", { id: short(openingPrint.shiftId) })}
                dateLabel={new Date().toLocaleDateString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz })}
                docNumber={short(openingPrint.shiftId)}
                office={printOffice}
                customerName={openingPrint.owner}
                columns={[
                  { header: t("mt.op_col_box"), key: "name" },
                  { header: t("mt.op_col_new"), key: "newBal", numeric: true },
                  { header: t("mt.op_col_old"), key: "oldBal", numeric: true },
                  { header: t("mt.op_col_diff"), key: "diff", numeric: true },
                ]}
                rows={openingPrint.rows}
                summary={[
                  { label: t("mt.op_time"), value: openingPrint.at },
                  { label: t("mt.op_operator"), value: openingPrint.owner },
                  { label: t("mt.op_notes"), value: openingPrint.note },
                ]}
              />
            ) : printing === "shift" && shiftReport ? (
              <div id="professional-print-template">
                <ShiftReportPages d={shiftReport} />
              </div>
            ) : printing === "day" && dayReport ? (
              <div id="professional-print-template">
                <DayReportPages d={dayReport} />
              </div>
            ) : null}
          </div>,
          document.body,
        )}
    </>
  );
}
